import React, { useState, useEffect } from 'react'

const ToggleSwitch = ({ id, checked, onChange, disabled }) => {
  return (
    <label htmlFor={id} className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={id}
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-brand-light-green peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-green"></div>
    </label>
  );
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    functional: false,
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent')
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => setIsVisible(false), 500)
  }

  const handleSave = (newPreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: newPreferences,
    }))
    handleClose()
  }

  const handleAcceptAll = () => {
    const allPrefs = { analytics: true, marketing: true, functional: true }
    setPreferences(allPrefs)
    handleSave(allPrefs)
  }

  const handleDeclineAll = () => {
    const noPrefs = { analytics: false, marketing: false, functional: false }
    setPreferences(noPrefs)
    handleSave(noPrefs)
  }

  const handleSavePreferences = () => {
    handleSave(preferences)
  }

  const handleToggle = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  if (!isVisible) return null

  const animationClass = isClosing
    ? 'opacity-0 translate-y-8'
    : 'opacity-100 translate-y-0'

  const backdropAnimationClass = isClosing ? 'opacity-0' : 'opacity-100'

  return (
    <div className={`fixed inset-0 z-50 flex items-end justify-center p-4 transition-opacity duration-300 ${backdropAnimationClass} ${!showPreferences ? 'pointer-events-none' : 'bg-black/20 backdrop-blur-sm pointer-events-auto'}`}>
      <div className={`bg-white rounded-lg shadow-2xl p-6 border border-gray-200/80 w-full max-w-lg transition-all duration-500 ease-in-out transform ${animationClass} pointer-events-auto`}>
        {showPreferences ? (
          // Preferences View
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-brand-green">Preferenze Cookie</h3>
              <button onClick={() => setShowPreferences(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-4 text-sm text-brand-text/80">
              <div className="flex justify-between items-center p-3 rounded-md bg-brand-subtle-bg">
                <div>
                  <h4 className="font-semibold text-brand-green">Cookie Necessari</h4>
                  <p>Essenziali per il funzionamento del sito.</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-brand-green text-white rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  </div>
                </div>
              </div>
              {Object.entries(preferences).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center p-3">
                  <div>
                    <h4 className="font-semibold text-brand-green capitalize">Cookie {key === 'analytics' ? 'Analitici' : key}</h4>
                    <p>{
                      key === 'analytics' ? 'Ci aiutano a migliorare il sito.' :
                      key === 'marketing' ? 'Per annunci personalizzati.' :
                      'Migliorano l\'esperienza utente.'
                    }</p>
                  </div>
                  <ToggleSwitch id={key} checked={value} onChange={() => handleToggle(key)} />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPreferences(false)} className="px-5 py-2.5 text-sm font-medium text-brand-text bg-gray-100 hover:bg-gray-200 rounded-md w-full transition">Annulla</button>
              <button onClick={handleSavePreferences} className="px-5 py-2.5 text-sm font-medium text-white bg-brand-green hover:bg-brand-light-green rounded-md w-full transition">Salva Preferenze</button>
            </div>
          </div>
        ) : (
          // Initial Banner View
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-brand-green/10 rounded-full text-brand-green mt-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-brand-green">Rispettiamo la tua privacy</h3>
                <p className="text-sm text-brand-text/70 mt-1">
                  Utilizziamo cookie per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti. Puoi scegliere quali cookie accettare.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button onClick={handleAcceptAll} className="px-4 py-2 text-sm font-medium text-white bg-brand-green hover:bg-brand-light-green rounded-md transition">Accetta tutti</button>
              <button onClick={handleDeclineAll} className="px-4 py-2 text-sm font-medium text-brand-text bg-gray-100 hover:bg-gray-200 rounded-md transition">Rifiuta tutti</button>
              <button onClick={() => setShowPreferences(true)} className="px-4 py-2 text-sm font-medium text-brand-green border border-brand-green/50 hover:bg-brand-green/5 rounded-md transition">Personalizza</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CookieConsent